import React, { useEffect, useState } from 'react';
import { format, parse, parseISO, isValid, setMonth as dfSetMonth, setYear as dfSetYear, startOfMonth } from 'date-fns';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Calendar } from './ui/calendar';

const MONTHS = Array.from({ length: 12 }, (_, i) => format(new Date(2000, i, 1), 'MMMM'));
const FORMATS = ['yyyy-MM-dd', 'yyyy/MM/dd', 'MM/dd/yyyy', 'M/d/yyyy', 'M/d/yy', 'MMM d yyyy', 'MMM d, yyyy', 'MMMM d yyyy', 'MMMM d, yyyy', 'd MMM yyyy', 'MMM d', 'MMMM d', 'MMM yyyy', 'MMMM yyyy', 'yyyy-MM'];

// "2024-06-15", "6/15/2024", "Jun 15 2024", "june 2024" … → Date (or null)
export const parseTypedDate = (s) => {
  const t = (s || '').trim().replace(/\s+/g, ' ');
  if (!t) return null;
  for (const f of FORMATS) {
    const d = parse(t, f, new Date());
    if (isValid(d) && d.getFullYear() > 1900 && d.getFullYear() < 2200) return d;
  }
  const d = new Date(t);
  return isValid(d) && d.getFullYear() > 1900 && d.getFullYear() < 2200 ? d : null;
};

// Popup calendar picker that stores YYYY-MM-DD strings. Type the date, or jump straight to a month / year —
// no clicking the arrow 24 times to log something done two years ago.
export const DatePickerField = ({ value, onChange, min, placeholder = 'Select date', className = '', testid, clearable = true }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [bad, setBad] = useState(false);
  const iso = (value || '').slice(0, 10);
  const selected = iso ? parseISO(iso) : undefined;
  const minDate = min ? parseISO(min.slice(0, 10)) : undefined;
  const [month, setMonth] = useState(startOfMonth(selected || minDate || new Date()));
  useEffect(() => { if (open) { setMonth(startOfMonth(selected || minDate || new Date())); setTyped(''); setBad(false); } }, [open, iso, min]);

  const thisYear = new Date().getFullYear();
  const firstYear = Math.min(minDate ? minDate.getFullYear() : 1990, selected ? selected.getFullYear() : 1990, 1990);
  const years = Array.from({ length: thisYear + 6 - firstYear }, (_, i) => firstYear + i);

  const pick = (d) => { onChange(format(d, 'yyyy-MM-dd')); setOpen(false); };
  const applyTyped = () => {
    const d = parseTypedDate(typed);
    if (!d) { setBad(!!typed.trim()); return; }
    if (minDate && d < minDate) { setBad(true); return; }
    pick(d);
  };
  const tid = (s) => (testid ? `${testid}-${s}` : undefined);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className={`${className} text-left flex items-center justify-between gap-2`}>
          <span className={iso ? 'text-slate-900' : 'text-slate-500'}>{iso ? format(selected, 'EEE, MMM d, yyyy') : placeholder}</span>
          <span className="flex items-center gap-1 shrink-0">
            {clearable && iso && (
              <X size={13} className="text-slate-500 hover:text-red-500" data-testid={tid('clear')}
                onClick={(e) => { e.stopPropagation(); onChange(''); }} />
            )}
            <CalendarIcon size={15} className="text-slate-500" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-[80]" align="start">
        <div className="px-3 pt-3 space-y-2">
          <div className="flex gap-1.5">
            <input value={typed} onChange={(e) => { setTyped(e.target.value); setBad(false); }} autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyTyped(); } }}
              placeholder="Type a date — 2024-06-15 or Jun 15 2024" data-testid={tid('typed')}
              className={`flex-1 min-w-0 h-9 px-2.5 rounded-lg border text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-blue-300 ${bad ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white'}`} />
            <button type="button" onClick={applyTyped} disabled={!typed.trim()} data-testid={tid('typed-go')}
              className="h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-bold disabled:opacity-40">Set</button>
          </div>
          {bad && <p className="text-[11px] text-red-700" data-testid={tid('typed-error')}>Couldn't read that date{minDate ? ` (must be on/after ${format(minDate, 'MMM d, yyyy')})` : ''} — try 2024-06-15</p>}
          <div className="flex gap-1.5">
            <select value={month.getMonth()} onChange={(e) => setMonth(dfSetMonth(month, Number(e.target.value)))} data-testid={tid('month')}
              className="flex-1 h-9 px-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-300">
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <select value={month.getFullYear()} onChange={(e) => setMonth(dfSetYear(month, Number(e.target.value)))} data-testid={tid('year')}
              className="w-24 h-9 px-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-300">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <Calendar mode="single" selected={selected} month={month} onMonthChange={setMonth}
          disabled={minDate ? { before: minDate } : undefined}
          onSelect={(d) => { if (d) pick(d); }} />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerField;
